import { useEffect } from 'react';
import { useShallow } from 'zustand/shallow';
import { useCompanies } from './useCompanies';
import { useUserTenants } from './api/useUserTenants';
import { useProjectStore, type Company } from '../store/projectStore';
import { getCurrentTenantId } from '../store/tenantStorage';

export function resolveCompanyForTenant({
  companies,
  currentTenantId,
}: {
  companies: Company[];
  currentTenantId: string | null;
}): Company | null {
  if (!currentTenantId) {
    return null;
  }

  const matchingCompanies = companies.filter(
    (company) => company.tenantId === currentTenantId,
  );

  return matchingCompanies[0] ?? null;
}

export function shouldAutoSelectCompany({
  companyForTenant,
  companyId,
  selectedCompanyTenantId,
  currentTenantId,
}: {
  companyForTenant: Company | null;
  companyId: string;
  selectedCompanyTenantId: string | null | undefined;
  currentTenantId: string | null;
}): boolean {
  if (!companyForTenant || !currentTenantId) {
    return false;
  }

  if (companyId !== companyForTenant.id) {
    return true;
  }

  return selectedCompanyTenantId !== currentTenantId;
}

export function isCompanyReadyForApplications({
  companyId,
  companyForTenant,
  selectedCompanyTenantId,
  currentTenantId,
}: {
  companyId: string;
  companyForTenant: Company | null;
  selectedCompanyTenantId?: string | null;
  currentTenantId?: string | null;
}): boolean {
  if (companyForTenant) {
    return Boolean(companyId) && companyId === companyForTenant.id;
  }

  return Boolean(
    companyId && currentTenantId && selectedCompanyTenantId === currentTenantId,
  );
}

export function isResolvingCompanyState({
  isLoading,
  isAwaitingCompanyAutoSelect,
}: {
  isLoading: boolean;
  isAwaitingCompanyAutoSelect: boolean;
}): boolean {
  return isLoading || isAwaitingCompanyAutoSelect;
}

export function useAutoSelectCompany() {
  const { companies, isLoading, error, isFetching, refetch } = useCompanies();
  const { currentTenantId: authTenantId, isFetched: isTenantsFetched } =
    useUserTenants();
  const { companyId, selectedCompany, setCompany } = useProjectStore(
    useShallow((state) => ({
      companyId: state.companyId,
      selectedCompany: state.selectedCompany,
      setCompany: state.setCompany,
    })),
  );

  const currentTenantId = isTenantsFetched
    ? getCurrentTenantId(authTenantId)
    : null;
  const companyForTenant = resolveCompanyForTenant({
    companies,
    currentTenantId,
  });

  useEffect(() => {
    if (isLoading || !currentTenantId) {
      return;
    }

    if (
      !companyForTenant ||
      !shouldAutoSelectCompany({
        companyForTenant,
        companyId,
        selectedCompanyTenantId: selectedCompany?.tenantId,
        currentTenantId,
      })
    ) {
      return;
    }

    setCompany(companyForTenant);
  }, [
    companyForTenant,
    companyId,
    currentTenantId,
    isLoading,
    selectedCompany?.tenantId,
    setCompany,
  ]);

  const isCompanyReady = isCompanyReadyForApplications({
    companyId,
    companyForTenant,
    selectedCompanyTenantId: selectedCompany?.tenantId,
    currentTenantId,
  });

  const isAwaitingCompanyAutoSelect =
    !isLoading &&
    !isFetching &&
    companyForTenant !== null &&
    companyId !== companyForTenant.id;

  const isResolvingCompany = isResolvingCompanyState({
    isLoading,
    isAwaitingCompanyAutoSelect,
  });

  return {
    isCompanyReady,
    isResolvingCompany,
    companyResolveError: error,
    companyForTenant,
    currentTenantId,
    refetchCompanies: refetch,
  };
}
